import React, { useState, useEffect } from 'react';
import { Upload, Music, Trash2, Edit2, Save, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

const BeatsManagement = () => {
  const [beats, setBeats] = useState([]);
  const [isAddingBeat, setIsAddingBeat] = useState(false);
  const [editingBeat, setEditingBeat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    producer_name: 'Trap Spade',
    bpm: '',
    key: '',
    lease_price: 40,
    unlimited_price: 80,
    exclusive_price: 150,
    audio_url: '',
    is_active: true
  });
  const [audioFile, setAudioFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchBeats();
  }, []);

  const fetchBeats = async () => {
    try {
      const response = await axios.get(`${API}/beats`);
      setBeats(response.data);
    } catch (error) {
      console.error('Error fetching beats:', error);
      toast.error('Failed to load beats');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav'];
      if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav)$/i)) {
        toast.error('Please upload an MP3 or WAV file');
        return;
      }
      setAudioFile(file);
    }
  };

  const uploadAudioFile = async () => {
    if (!audioFile) return null;
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', audioFile);
      
      const response = await axios.post(`${API}/upload-beat-audio`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data.url;
    } catch (error) {
      console.error('Error uploading audio:', error);
      toast.error('Failed to upload audio file');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let audioUrl = formData.audio_url;
      
      // If new file selected, upload it
      if (audioFile) {
        audioUrl = await uploadAudioFile();
        if (!audioUrl) {
          toast.error('Failed to upload audio file');
          return;
        }
      }
      
      const beatData = {
        ...formData,
        audio_url: audioUrl
      };
      
      if (editingBeat) {
        await axios.put(`${API}/beats/${editingBeat}`, beatData);
        toast.success('Beat updated successfully!');
      } else {
        if (!audioFile && !audioUrl) {
          toast.error('Please upload an audio file');
          return;
        }
        await axios.post(`${API}/beats`, beatData);
        toast.success('Beat added successfully!');
      }
      
      resetForm();
      fetchBeats();
    } catch (error) {
      console.error('Error saving beat:', error);
      toast.error('Failed to save beat. Please try again.');
    }
  };

  const handleDelete = async (beatId) => {
    if (!window.confirm('Are you sure you want to delete this beat?')) return;

    try {
      await axios.delete(`${API}/beats/${beatId}`);
      toast.success('Beat deleted successfully!');
      fetchBeats();
    } catch (error) {
      console.error('Error deleting beat:', error);
      toast.error('Failed to delete beat');
    }
  };

  const handleEdit = (beat) => {
    setEditingBeat(beat);
    setFormData({
      title: beat.title,
      producer_name: beat.producer_name || 'Trap Spade',
      bpm: beat.bpm,
      key: beat.key,
      lease_price: beat.lease_price,
      unlimited_price: beat.unlimited_price || 80,
      exclusive_price: beat.exclusive_price,
      audio_url: beat.audio_url,
      is_active: beat.is_active
    });
    setIsAddingBeat(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      producer_name: 'Trap Spade',
      bpm: '',
      key: '',
      lease_price: 40,
      unlimited_price: 80,
      exclusive_price: 150,
      audio_url: '',
      is_active: true
    });
    setAudioFile(null);
    setIsAddingBeat(false);
    setEditingBeat(null);
  };

  return (
    <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Music className="text-purple-400" size={24} />
          <h2 className="text-2xl font-bold text-white">Beats Management</h2>
        </div>
        <button
          onClick={() => setIsAddingBeat(!isAddingBeat)}
          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center space-x-2"
        >
          {isAddingBeat ? <X size={18} /> : <Upload size={18} />}
          <span>{isAddingBeat ? 'Cancel' : 'Add Beat'}</span>
        </button>
      </div>

      {/* Add/Edit Form */}
      {isAddingBeat && (
        <form onSubmit={handleSubmit} className="bg-black/40 rounded-lg p-6 mb-6 border border-purple-500/20">
          <h3 className="text-white font-semibold mb-4">
            {editingBeat ? 'Edit Beat' : 'Add New Beat'}
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Beat Title"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
            />
            
            <input
              type="text"
              placeholder="Producer Name (e.g., Trap Spade)"
              required
              value={formData.producer_name}
              onChange={(e) => setFormData({ ...formData, producer_name: e.target.value })}
              className="px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
            />
            
            <input
              type="number"
              placeholder="BPM"
              required
              value={formData.bpm}
              onChange={(e) => setFormData({ ...formData, bpm: e.target.value })}
              className="px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
            />
            
            <input
              type="text"
              placeholder="Key (e.g., Am, Dm)"
              required
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: e.target.value })}
              className="px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
            />
            
            <input
              type="number"
              placeholder="Lease Price ($40)"
              required
              value={formData.lease_price}
              onChange={(e) => setFormData({ ...formData, lease_price: Number(e.target.value) })}
              className="px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
            />
            
            <input
              type="number"
              placeholder="Unlimited Price ($80)"
              required
              value={formData.unlimited_price}
              onChange={(e) => setFormData({ ...formData, unlimited_price: Number(e.target.value) })}
              className="px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
            />
            
            <input
              type="number"
              placeholder="Exclusive Price ($150+)"
              required
              value={formData.exclusive_price}
              onChange={(e) => setFormData({ ...formData, exclusive_price: Number(e.target.value) })}
              className="px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
            />
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Audio File (MP3 or WAV) *
              </label>
              <input
                type="file"
                accept=".mp3,.wav,audio/mpeg,audio/wav"
                onChange={handleFileChange}
                className="w-full px-4 py-3 bg-black/40 border border-purple-500/20 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-purple-600 file:text-white file:cursor-pointer hover:file:bg-purple-700 focus:border-purple-500 focus:outline-none"
              />
              {audioFile && (
                <p className="text-green-400 text-sm mt-2">
                  ✓ Selected: {audioFile.name}
                </p>
              )}
              {formData.audio_url && !audioFile && (
                <p className="text-gray-400 text-sm mt-2">
                  Current file: {formData.audio_url.split('/').pop()}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2 mt-4">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4"
            />
            <label htmlFor="is_active" className="text-gray-300">Active (visible to customers)</label>
          </div>

          <div className="flex space-x-3 mt-6">
            <button
              type="submit"
              disabled={uploading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <Save size={20} />
                  <span>{editingBeat ? 'Update' : 'Add'} Beat</span>
                </>
              )}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-all"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Beats List */}
      <div className="space-y-3">
        {beats.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No beats added yet. Click &quot;Add Beat&quot; to get started.</p>
        ) : (
          beats.map((beat) => (
            <div
              key={beat.id}
              className="bg-black/40 border border-purple-500/20 rounded-lg p-4 flex items-center justify-between hover:border-purple-500/50 transition-all"
            >
              <div className="flex-1">
                <h3 className="text-white font-semibold mb-1">{beat.title}</h3>
                <div className="flex items-center space-x-4 text-sm text-gray-400">
                  <span className="text-purple-300 font-medium">by {beat.producer_name || 'Trap Spade'}</span>
                  <span>•</span>
                  <span>{beat.bpm} BPM</span>
                  <span>•</span>
                  <span>Key: {beat.key}</span>
                  <span>•</span>
                  <span className="text-purple-400">Lease: ${beat.lease_price}</span>
                  <span>•</span>
                  <span className="text-blue-400">Unlimited: ${beat.unlimited_price || 80}</span>
                  <span>•</span>
                  <span className="text-green-400">Exclusive: ${beat.exclusive_price}</span>
                  {!beat.is_active && (
                    <>
                      <span>•</span>
                      <span className="text-red-400">Inactive</span>
                    </>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleEdit(beat)}
                  className="p-2 bg-blue-900/30 hover:bg-blue-900/50 rounded-lg transition-colors"
                >
                  <Edit2 size={18} className="text-blue-400" />
                </button>
                <button
                  onClick={() => handleDelete(beat.id)}
                  className="p-2 bg-red-900/30 hover:bg-red-900/50 rounded-lg transition-colors"
                >
                  <Trash2 size={18} className="text-red-400" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BeatsManagement;
